import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { isAndroidApp } from "../../tools/android";
import { isIOSApp } from "../../tools/ios";
import { getCurrentOS } from "../../tools/utils";

/** Running in either iOS or Android native app */
export const isNativeApp = () => isIOSApp() || isAndroidApp();

const DESKTOP_MIN_WIDTH = 768;

/**
 * A screen whose SHORTEST side is a desktop width: a large tablet (a Galaxy
 * Tab A9+ is 800 CSS px wide in portrait, an iPad Air 820), or a computer. It
 * gets the desktop layout, in a native shell as in a browser. The shortest
 * side, not the current width, so a rotation never swaps the layout. The
 * Android shell applies the same rule to the same display to keep that layout
 * out of the system bars (keepLargeTabletInsideSystemBars in
 * WebAppActivity.java).
 */
const isLargeScreen = () =>
  Math.min(globalThis.screen.width, globalThis.screen.height) >=
  DESKTOP_MIN_WIDTH;

/**
 * The desktop shell: the layout whose navigation is the sidebar. Chosen by the
 * USER AGENT, not the viewport width, so a narrowed desktop window keeps it,
 * and by the screen for a large tablet, whose user agent says mobile (iPad
 * Safari announces a Mac, but react-device-detect reads its touch points).
 */
export const isDesktopShell = () =>
  isLargeScreen() || (!isMobile && !isNativeApp());

/**
 * The client a Juisci GPT turn is asked from, recorded on the persisted turn.
 * The native shells are told apart by their bridge, the browsers by the user
 * agent: iPad Safari announces a Mac (`desktop_web`), an iPad in the iPhone app
 * is `ios_app`, an Android tablet is `android_web`.
 */
export const currentClient = () => {
  if (isIOSApp()) return "ios_app";
  if (isAndroidApp()) return "android_app";
  const os = getCurrentOS();
  switch (os) {
    case "ios":
      return "ios_web";
    case "android":
      return "android_web";
    case "web":
      return "desktop_web";
    default:
      throw new Error(os satisfies never);
  }
};

/**
 * Desktop layout: the desktop pages, in a viewport of 768px or more INSIDE the
 * desktop shell. Never outside it: the desktop pages leave the navigation to
 * the shell's sidebar and hide the phone's bottom bar, so a phone in landscape
 * (a Pro Max is 932px wide) or an iPad mini in landscape (1133px) would get no
 * navigation at all. They keep the phone's pages, stretched.
 */
export const isDesktopLayout = () =>
  globalThis.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches &&
  isDesktopShell();

/**
 * The desktop layout in a desktop browser: the one place where a signed-out
 * visitor meets the login page, the code modal and the logged-out JuisciGPT, a
 * kept tab, and a physical keyboard. A native shell on a large tablet has the
 * desktop layout but keeps the phone's front door (`/welcome`, its QR code
 * signup and its mailed-code recovery) and its software keyboard.
 */
export const isDesktopBrowserLayout = () => isDesktopLayout() && !isNativeApp();

/** Web browser on small screen (<768px), not in native app */
export const isMobileWeb = () => {
  return (
    !globalThis.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches &&
    !isNativeApp()
  );
};

export const useIsDesktopView = () => {
  const [isDesktop, setIsDesktop] = useState<boolean>(isDesktopLayout());

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia(
      `(min-width: ${DESKTOP_MIN_WIDTH}px)`
    );

    const handleChange = (event: MediaQueryListEvent): void => {
      setIsDesktop(event.matches && isDesktopShell());
    };

    setIsDesktop(mediaQuery.matches && isDesktopShell());

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return { isDesktop };
};

/**
 * Whether the window is at least the 1280px the Figma desktop grid is drawn
 * for (Tailwind `xl`). Reactive, since a desktop window is resized.
 */
export const useIsXlViewport = (): boolean => {
  const query = "(min-width: 1280px)";
  const [matches, setMatches] = useState<boolean>(
    globalThis.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return matches;
};
